/**
 * MailComposeModal — Antwort verfassen (Phase 5.1 + 5.2 D-09).
 *
 *  - To = lead.vereinEmail (editierbar — Sonderfaelle moeglich)
 *  - From = teamshops@anstoss24.de (read-only, kein From-Spoofing erlaubt)
 *  - Subject = "Re: <originaler-Subject>" (editierbar)
 *  - Body = TipTap-WYSIWYG-Editor (D-09, ersetzt Plaintext-Textarea aus 5.1)
 *  - Signatur wird automatisch von Backend (renderEmailHtml + getActorSignature) am Mail-Ende angehaengt
 *  - Senden ruft Backend POST /leads/:id/reply mit { to, subject, body, bodyHtml }
 *  - bodyHtml im Frontend mit DOMPurify (sanitizeEditorHtml) gewaescht
 *  - body (Plaintext) wird aus bodyHtml extrahiert (DOMParser textContent) — fuer Mail-Clients ohne HTML
 *  - Bei Allowlist-Miss im Dry-Run: Tool zeigt simulated-Hinweis
 */

import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@libero/core/components/ui/button';
import { leadsApi } from '../services/leadsApi';
import { TipTapEditor } from './editor/TipTapEditor';
import { sanitizeEditorHtml } from './editor/sanitizeEditorHtml';
import type {
  Lead,
  LeadTemplateKey,
  LeadTemplateProfile,
  SignatureProfile,
  TemplateProfilesGrouped,
} from '../types';

interface Props {
  lead: Lead;
  replyToSubject: string;
  replyToMessageId: string | null;
  onClose: () => void;
  onSent: () => void;
  // Bei einer echten Antwort (aus dem Mailverlauf-Tab) wird KEINE Vorlage
  // vorausgewaehlt — der Editor startet leer, der Bearbeiter schreibt frei.
  // Nur beim Erstkontakt-/Workflow-Versand ist die Status-Default-Vorlage sinnvoll.
  isReply?: boolean;
}

function htmlToPlainText(html: string): string {
  // DOMParser im Browser; Server-Side haben wir hier nicht
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').trim();
}

// Mapping Lead-Status → Vorlagen-Stage fuer Initial-Auswahl beim Modal-Open (D-12)
// Nur die 3 Workflow-Stages mappen; andere Stages (qualifiziert, hot_lead etc.)
// bekommen keinen Default-Pick — User waehlt manuell.
const STATUS_TO_STAGE: Record<string, LeadTemplateKey | null> = {
  in_kontakt: 'erstkontakt',
  follow_up_1: 'followup_1',
  follow_up_2: 'followup_2',
};

const STAGE_LABELS: Record<LeadTemplateKey, string> = {
  erstkontakt: 'Erstkontakt',
  followup_1: 'Follow-up 1',
  followup_2: 'Follow-up 2',
};

function findProfileById(
  grouped: TemplateProfilesGrouped | null,
  id: number,
): LeadTemplateProfile | undefined {
  if (!grouped) return undefined;
  for (const stage of ['erstkontakt', 'followup_1', 'followup_2'] as const) {
    const found = grouped[stage].find((p) => p.id === id);
    if (found) return found;
  }
  return undefined;
}

// Variablen-Replace mit Lead-Daten — fehlende Werte bleiben als {{...}}
function replaceVars(text: string, lead: Lead): string {
  const vars: Record<string, string> = {
    verein_name: lead.vereinName ?? '',
    verein_contact_person: lead.vereinContactPerson || lead.vereinName || '',
    verein_email: lead.vereinEmail ?? '',
  };
  return text.replace(/\{\{(\w+)\}\}/g, (m, k) => vars[k] ?? m);
}

// Pruefen ob der Editor signifikant gefuellt ist (mehr als nur <p></p>).
// Wird genutzt um zu entscheiden ob ein Overwrite-Confirm noetig ist.
function editorHasContent(html: string): boolean {
  return html.replace(/<[^>]+>/g, '').trim().length > 0;
}

export default function MailComposeModal({
  lead,
  replyToSubject,
  onClose,
  onSent,
  isReply = false,
}: Props) {
  const [to, setTo] = useState(lead.vereinEmail ?? '');
  const [subject, setSubject] = useState(
    replyToSubject ? `Re: ${replyToSubject.replace(/^Re:\s*/i, '')}` : '',
  );
  const [bodyHtml, setBodyHtml] = useState<string>('<p></p>');
  const [hasSignature, setHasSignature] = useState<boolean>(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [simulatedInfo, setSimulatedInfo] = useState<string | null>(null);

  // Plan 07-04: Signatur-Profile laden + Default vorauswaehlen
  const [profiles, setProfiles] = useState<SignatureProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);

  // Phase 8 Plan 04: Vorlagen-Profile (D-04) — Dropdown im Compose-Modal
  const [templateProfiles, setTemplateProfiles] = useState<TemplateProfilesGrouped | null>(null);
  const [selectedTemplateProfileId, setSelectedTemplateProfileId] = useState<number | null>(null);

  useEffect(() => {
    leadsApi.signatureProfiles.list()
      .then((profs) => {
        setProfiles(profs);
        const defaultProf = profs.find((p) => p.isDefault);
        if (defaultProf) setSelectedProfileId(defaultProf.id);
      })
      .catch(() => { /* profiles bleibt leer, kein Crash */ });
  }, []);

  // Vorlagen laden + initiale Auswahl basierend auf Lead-Status (D-12)
  useEffect(() => {
    leadsApi.templateProfiles.list()
      .then((grouped) => {
        setTemplateProfiles(grouped);
        // Antwort-Fall: keine Vorlage vorbelegen — Editor bleibt leer, Betreff bleibt "Re: ...".
        if (isReply) return;
        const stage = STATUS_TO_STAGE[lead.status];
        if (!stage) return;
        const defaultOfStage = grouped[stage]?.find((p) => p.isDefault);
        if (defaultOfStage) {
          // Vorbelegung: Subject + Body aus der Default-Vorlage. Der initiale
          // Bestand des Editors ist <p></p> — kein User-Text, kein Confirm noetig.
          setSubject(replaceVars(defaultOfStage.subject, lead));
          setBodyHtml(replaceVars(defaultOfStage.bodyHtml, lead) || '<p></p>');
          setSelectedTemplateProfileId(defaultOfStage.id);
        }
      })
      .catch(() => { /* templateProfiles bleibt null, kein Crash */ });
    // Run once auf Mount mit dem ersten lead-Snapshot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyTemplateProfile(profile: LeadTemplateProfile) {
    setSubject(replaceVars(profile.subject, lead));
    setBodyHtml(replaceVars(profile.bodyHtml, lead) || '<p></p>');
    setSelectedTemplateProfileId(profile.id);
  }

  function handleTemplateProfileChange(idStr: string) {
    if (!idStr) {
      // "Keine — leer starten" — wenn der Editor schon gefuellt ist, Confirm
      setSelectedTemplateProfileId(null);
      return;
    }
    const id = Number(idStr);
    const profile = findProfileById(templateProfiles, id);
    if (!profile) return;
    if (editorHasContent(bodyHtml)) {
      const confirmed = window.confirm(
        `Vorlage "${profile.name}" einfuegen?\n\nDein aktueller Text wird ersetzt.`,
      );
      if (!confirmed) return;
    }
    applyTemplateProfile(profile);
  }

  // Pruefe ob User eine Signatur hinterlegt hat (Hinweistext — Fallback wenn keine Profile)
  useEffect(() => {
    leadsApi.signature.getMine()
      .then((sig) => setHasSignature(sig.signatureHtml.trim().length > 0))
      .catch(() => setHasSignature(false));
  }, []);

  function isValidEmail(s: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
  }

  async function handleSend() {
    if (!isValidEmail(to)) {
      setError('Bitte gueltige Empfaenger-Mail eingeben');
      return;
    }
    if (!subject.trim()) {
      setError('Betreff darf nicht leer sein');
      return;
    }
    const sanitized = sanitizeEditorHtml(bodyHtml);
    const plainText = htmlToPlainText(sanitized);
    if (!plainText.trim()) {
      setError('Nachricht darf nicht leer sein');
      return;
    }
    setSending(true);
    setError(null);
    setSimulatedInfo(null);
    try {
      const res = await leadsApi.reply(lead.id, {
        to: to.trim(),
        subject: subject.trim(),
        body: plainText,
        bodyHtml: sanitized,
        signatureProfileId: selectedProfileId,
        templateProfileId: selectedTemplateProfileId,
      });
      if (res?.simulated) {
        setSimulatedInfo(res.message ?? 'Test-Modus: Mail nur in DB gespeichert, kein Versand');
        setTimeout(() => onSent(), 1800);
      } else {
        onSent();
      }
    } catch (e: any) {
      setError(e.response?.data?.error?.message ?? 'Senden fehlgeschlagen');
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#1A1A1A]">Antwort verfassen</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-[#DAD9D9]/40 rounded"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}
        {simulatedInfo && (
          <div className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-300 rounded p-2">
            {simulatedInfo}
          </div>
        )}

        {/* Phase 8 Plan 04 (D-04): Vorlagen-Dropdown — gruppiert nach Stage, Default
            der Lead-Stage vorausgewaehlt (D-12). Wechsel zeigt Confirm wenn
            der Editor schon gefuellt ist. */}
        {templateProfiles && (
          (templateProfiles.erstkontakt.length > 0
            || templateProfiles.followup_1.length > 0
            || templateProfiles.followup_2.length > 0) && (
            <div>
              <label className="text-sm font-medium block mb-1">Vorlage</label>
              <select
                className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-sm"
                value={selectedTemplateProfileId ?? ''}
                onChange={(e) => handleTemplateProfileChange(e.target.value)}
              >
                <option value="">(Keine — leer starten)</option>
                {(['erstkontakt', 'followup_1', 'followup_2'] as const).map((stage) => (
                  templateProfiles[stage].length > 0 ? (
                    <optgroup key={stage} label={STAGE_LABELS[stage]}>
                      {templateProfiles[stage].map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}{p.isDefault ? ' ⭐' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ) : null
                ))}
              </select>
            </div>
          )
        )}

        {/* Plan 07-04: Signatur-Dropdown — zeigt alle eigenen Profile, Default vorausgewaehlt */}
        {profiles.length > 0 && (
          <div>
            <label className="text-sm font-medium block mb-1">Signatur</label>
            <select
              className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-sm"
              value={selectedProfileId ?? ''}
              onChange={(e) => setSelectedProfileId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Keine Signatur</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.isDefault ? ' (Standard)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="text-sm font-medium block mb-1">An <span className="text-red-500">*</span></label>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="empfaenger@verein.de"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Vorbelegt mit der Vereins-Adresse aus dem Plenty-Formular. Bei Sonderfaellen aenderbar.
          </p>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Von</label>
          <input
            className="w-full border rounded px-3 py-2 text-sm bg-[#DAD9D9]/30 text-muted-foreground"
            value="teamshops@anstoss24.de"
            readOnly
            disabled
          />
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Betreff <span className="text-red-500">*</span></label>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Re: ..."
            maxLength={200}
          />
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Nachricht <span className="text-red-500">*</span></label>
          <TipTapEditor
            content={bodyHtml}
            onChange={setBodyHtml}
            placeholder="Hallo, ..."
          />
          {/* Hinweistext nur wenn noch keine Profile vorhanden (Fallback fuer alte Signatur-Tabelle) */}
          {profiles.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {hasSignature
                ? 'Deine Signatur wird automatisch angehaengt.'
                : 'Hinweis: Du hast noch keine Signatur hinterlegt. Lege sie unter "Mail-Vorlagen" an.'}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={sending}>
            Abbrechen
          </Button>
          <Button
            size="sm"
            onClick={() => void handleSend()}
            disabled={sending || !to || !subject}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            {sending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            {sending ? 'Sendet...' : 'Senden'}
          </Button>
        </div>
      </div>
    </div>
  );
}
